// Backward-compatible shim → forwards every call to the new TS logger in
// `src/shared/observability/logger.ts`. Preserves the legacy signature
// `logger.{level}(module, message, context)` so 200+ existing call sites
// keep working without edits.
//
// Translation rules applied per call:
//   • Recognized LogInput fields on `context` (event, action, component,
//     method, endpoint, status, durationMs, branch, data, error,
//     correlationId, route) are lifted to the top of the new input.
//   • Everything else on `context` is bundled into `data` so legacy
//     callers don't lose information.
//   • If `event` is missing, the new logger derives one from the message
//     and marks the record `legacyMissingEvent: true` for Phase 5.6 cleanup.
//
// Source override:
//   • The legacy logger inspected `context.source.{file,method,line,column}`
//     to inject the call site. The new TS logger captures the full context
//     elsewhere (component/module). To avoid losing this, we drop `source`
//     entirely from `context` here so the new logger does not put a
//     {file,method} blob inside `data`.

import { logger as nextLogger } from '../observability/logger';

const KNOWN_FIELDS = new Set([
  'event',
  'action',
  'component',
  'module',
  'route',
  'method',
  'endpoint',
  'status',
  'durationMs',
  'branch',
  'data',
  'error',
  'correlationId',
]);

function translate(context) {
  if (context == null) return {};
  if (context instanceof Error) return { error: context };
  if (typeof context !== 'object' || Array.isArray(context)) {
    return { data: { value: context } };
  }

  const out = {};
  const extras = {};
  for (const [key, value] of Object.entries(context)) {
    if (key === 'source') continue; // legacy source-override frame; ignore
    if (KNOWN_FIELDS.has(key)) {
      out[key] = value;
    } else {
      extras[key] = value;
    }
  }
  if (Object.keys(extras).length > 0) {
    out.data = { ...(out.data || {}), ...extras };
  }
  return out;
}

function forward(level, moduleName, message, context) {
  try {
    nextLogger[level](moduleName, String(message ?? ''), translate(context));
  } catch {
    // Never let logging crash the caller.
  }
}

// ---------------------------------------------------------------------------
// Legacy createMethodLogger — preserved for API parity. No external callers
// currently use it (verified at shim time), but the export must stay so any
// tree-shaken-but-still-imported reference doesn't blow up.
// ---------------------------------------------------------------------------

export const createMethodLogger = (moduleName, methodName, fileName = '') => {
  const base = { component: methodName };
  if (fileName) base.data = { file: fileName };
  const withDefaults = (ctx) => ({ ...base, ...(ctx || {}) });
  return Object.freeze({
    start: (message = 'Method started', context = {}) =>
      forward('debug', moduleName, message, withDefaults(context)),
    success: (message = 'Method succeeded', context = {}) =>
      forward('info', moduleName, message, withDefaults(context)),
    warn: (message = 'Method warning', context = {}) =>
      forward('warn', moduleName, message, withDefaults(context)),
    failure: (message = 'Method failed', context = {}) =>
      forward('error', moduleName, message, withDefaults(context)),
  });
};

export const logger = Object.freeze({
  debug: (moduleName, message, context) => forward('debug', moduleName, message, context),
  info: (moduleName, message, context) => forward('info', moduleName, message, context),
  warn: (moduleName, message, context) => forward('warn', moduleName, message, context),
  error: (moduleName, message, context) => forward('error', moduleName, message, context),
  createMethodLogger,
});

export default logger;
